// Kept import-free so it stays unit-testable in vitest's node environment.
// Kept identical to apps/admin/src/lib/ws-auth.ts and apps/agency/src/lib/ws-auth.ts.

/**
 * How the realtime WebSocket authenticates (security plan Phase 4).
 *
 * The app used to connect with its access token (JWT) in the URL, which ends up
 * in logs. Now it first asks the API for a one-time ticket
 * (`POST /api/realtime/ws-ticket`, valid 60 s, single use) and connects with
 * `?ticket=` instead.
 *
 * The rule that matters: this change must never cost anyone their connection.
 * So the old way stays as the fallback, used when:
 *   - the ticket request fails for any reason (no network, an API that does not
 *     have the endpoint yet, Redis down: 404, 503, timeout...), for that same
 *     attempt, with no extra wait;
 *   - a socket opened with a ticket fails before it ever opens (a server that
 *     does not accept tickets yet): the next attempt uses the token. If that
 *     one then works, tickets are skipped for TOKEN_ONLY_MS, so a server
 *     without ticket support costs at most one failed attempt per window
 *     rather than one per reconnect.
 */

/** Path of the ticket endpoint, relative to an API base that ends in `/api`. */
export const WS_TICKET_PATH = "/realtime/ws-ticket";
/** A ticket request slower than this is abandoned and the token is used. */
export const WS_TICKET_TIMEOUT_MS = 5_000;
/** After the ticket path failed and the token worked, use the token this long. */
export const TOKEN_ONLY_MS = 10 * 60_000;

export type WsAuthKind = "ticket" | "token";

export interface WsAuth {
    kind: WsAuthKind;
    /** The query-string part, e.g. `ticket=abc`. */
    query: string;
}

type FetchLike = (
    url: string,
    init: { method: string; headers: Record<string, string>; signal?: AbortSignal }
) => Promise<{ ok: boolean; json: () => Promise<unknown> }>;

/**
 * Asks the API for a one-time ticket. Never throws: any failure answers null
 * and the caller connects with its token, exactly as before this change.
 * Deliberately not the app's normal request helper, so a failure here can
 * never refresh, log out or report anything.
 */
export async function fetchWsTicket(
    apiBase: string,
    accessToken: string,
    fetchImpl: FetchLike,
    timeoutMs: number = WS_TICKET_TIMEOUT_MS
): Promise<string | null> {
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<null>((resolve) => {
        timer = setTimeout(() => {
            controller?.abort();
            resolve(null);
        }, timeoutMs);
    });
    const request = (async (): Promise<string | null> => {
        try {
            const res = await fetchImpl(`${apiBase}${WS_TICKET_PATH}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${accessToken}` },
                signal: controller?.signal
            });
            if (!res.ok) {
                return null;
            }
            const body = (await res.json()) as { ticket?: unknown } | null;
            const ticket = body?.ticket;
            return typeof ticket === "string" && /^[A-Za-z0-9_-]{20,200}$/.test(ticket)
                ? ticket
                : null;
        } catch {
            return null;
        }
    })();
    try {
        return await Promise.race([request, timeout]);
    } finally {
        if (timer) {
            clearTimeout(timer);
        }
    }
}

export function ticketAuth(ticket: string): WsAuth {
    return { kind: "ticket", query: `ticket=${encodeURIComponent(ticket)}` };
}

/** The old way, kept for fallback. Unchanged from before (`token=<jwt>`). */
export function tokenAuth(accessToken: string): WsAuth {
    return { kind: "token", query: `token=${accessToken}` };
}

/**
 * Remembers how recent connects went and decides whether the next one tries a
 * ticket. One instance per app (module-level), so it survives re-renders.
 */
export function createWsAuthPolicy(now: () => number = Date.now) {
    let ticketFailedLast = false;
    let tokenOnlyUntil = 0;
    let fellBack = false;
    return {
        /** Should the next connect ask for a ticket? */
        shouldTryTicket(): boolean {
            return !ticketFailedLast && now() >= tokenOnlyUntil;
        },
        /** The socket opened. */
        opened(kind: WsAuthKind): void {
            if (kind === "token" && fellBack) {
                // Tickets failed and the token worked: the server side does not
                // take tickets (yet). Stop paying a failed attempt per reconnect.
                tokenOnlyUntil = now() + TOKEN_ONLY_MS;
            }
            ticketFailedLast = false;
            fellBack = false;
        },
        /** The socket closed without ever opening. */
        failedBeforeOpen(kind: WsAuthKind): void {
            if (kind === "ticket") {
                ticketFailedLast = true;
                fellBack = true;
            } else {
                // Token failed too (offline, expired login): nothing to learn
                // about tickets; try one again next time.
                ticketFailedLast = false;
                fellBack = false;
            }
        }
    };
}

export type WsAuthPolicy = ReturnType<typeof createWsAuthPolicy>;

/**
 * The auth part of the next connect URL: a ticket when the policy allows and
 * the API hands one out, otherwise the token.
 */
export async function chooseWsAuth(
    policy: WsAuthPolicy,
    accessToken: string,
    getTicket: (accessToken: string) => Promise<string | null>
): Promise<WsAuth> {
    if (policy.shouldTryTicket()) {
        const ticket = await getTicket(accessToken);
        if (ticket) {
            return ticketAuth(ticket);
        }
    }
    return tokenAuth(accessToken);
}
